import type { DrinkId } from '../engine/bar';

/**
 * Each drink in the glass it comes in, with one detail for what it does
 * (chosen 2026-09-27: "the glass" direction). Drawn in ink for the cream
 * menu cards; the fills are the drink's own colour.
 *
 * Every icon is two layers: the liquid, then the glass drawn over it, so
 * ordering a drink can tip the glass and drain the liquid (user,
 * 2026-09-27: "it looks like it's being drunk").
 */
const INK = '#2a1e14';

function Liquid({ id }: { id: DrinkId }) {
  switch (id) {
    case 'water':
      return (
        <>
          <path d="M14 20h20l-2 20H16z" fill="#bfe3ef" />
          <circle cx="20" cy="30" r="1.8" fill="#fbf7ee" />
          <circle cx="27" cy="26" r="1.4" fill="#fbf7ee" />
        </>
      );
    case 'coffee':
      return <path d="M12 18h20v14a7 7 0 0 1-7 7h-6a7 7 0 0 1-7-7z" fill="#6b4428" />;
    case 'lager':
      return (
        <>
          <path d="M13 13h18v25a3 3 0 0 1-3 3h-12a3 3 0 0 1-3-3z" fill="#e0b85e" />
          <path d="M12 12c0-4 4-5 6-4 1-3 7-3 8 0 2-1 6 0 6 4z" fill="#fbf7ee" />
        </>
      );
    case 'pour':
      return (
        <>
          <path d="M11 22h22l-1.6 16H12.6z" fill="#c9793a" />
          <rect x="15" y="24" width="7" height="7" rx="1" fill="#fbf7ee" fillOpacity="0.7" />
        </>
      );
    case 'cosmo':
      return <path d="M9 12h28L23 25z" fill="#e2708a" />;
    case 'espresso':
      return (
        <>
          <path d="M9 12h28L23 25z" fill="#3b2418" />
          <path d="M11 13h24" stroke="#e8d6a8" strokeWidth="3" />
          <ellipse cx="19" cy="10" rx="2" ry="1.3" fill="#6b4428" />
          <ellipse cx="23" cy="9" rx="2" ry="1.3" fill="#6b4428" />
          <ellipse cx="27" cy="10" rx="2" ry="1.3" fill="#6b4428" />
        </>
      );
    case 'happy':
      return <path d="M14 18h16l-1.8 22H15.8z" fill="#f0a24a" />;
    case 'lastcall':
      return <path d="M16 22h12l-1.8 16H17.8z" fill="#e0b85e" />;
    case 'topshelf':
      return <path d="M20 38V20l-2-3v-5h8v5l-2 3v18z" fill="#7a2a22" />;
  }
}

function Glass({ id }: { id: DrinkId }) {
  switch (id) {
    case 'water':
      return (
        <>
          <path d="M13 10h22l-3 30H16z" fill="none" stroke={INK} strokeWidth="2" />
          <path d="M15 20h18" stroke={INK} strokeWidth="1.5" />
        </>
      );
    case 'coffee':
      return (
        <>
          <path d="M11 16h22v16a8 8 0 0 1-8 8h-6a8 8 0 0 1-8-8z" fill="none" stroke={INK} strokeWidth="2" />
          <path d="M33 20h3a4 4 0 0 1 0 8h-3" fill="none" stroke={INK} strokeWidth="2" />
          <path d="M18 6c-2 3 2 4 0 7M24 6c-2 3 2 4 0 7" fill="none" stroke="#a8873c" strokeWidth="1.5" strokeLinecap="round" className="steam" />
        </>
      );
    case 'lager':
      return (
        <>
          <path d="M12 12h20v26a4 4 0 0 1-4 4h-12a4 4 0 0 1-4-4z" fill="none" stroke={INK} strokeWidth="2" />
          <path d="M32 17h4a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3h-4" fill="none" stroke={INK} strokeWidth="2" />
          {/* A four-leaf clover on the glass (user, 2026-09-27; was a heart). */}
          <g fill="#2f7a4a">
            <circle cx="19.6" cy="24.6" r="2.7" />
            <circle cx="24.4" cy="24.6" r="2.7" />
            <circle cx="19.6" cy="29.4" r="2.7" />
            <circle cx="24.4" cy="29.4" r="2.7" />
          </g>
          <path d="M22 27l3.5 7" stroke="#2f7a4a" strokeWidth="1.6" strokeLinecap="round" />
        </>
      );
    case 'pour':
      return (
        <>
          <path d="M10 18h24l-2 20H12z" fill="none" stroke={INK} strokeWidth="2" />
          <path d="M30 6l6 8" stroke="#a8873c" strokeWidth="3" strokeLinecap="round" />
          <path d="M33 12l-6 6" stroke="#a8873c" strokeWidth="1.5" strokeDasharray="2 2" />
        </>
      );
    case 'cosmo':
      return (
        <>
          <path d="M8 12h30L23 26z" fill="none" stroke={INK} strokeWidth="2" strokeLinejoin="round" />
          <path d="M23 26v12M16 40h14" stroke={INK} strokeWidth="2" strokeLinecap="round" />
          <circle cx="34" cy="10" r="5" fill="#c9a45c" stroke={INK} strokeWidth="1.5" />
          <path d="M34 7v6M32 8.5h3a1.5 1.5 0 0 1 0 3h-2" fill="none" stroke={INK} strokeWidth="1.2" />
        </>
      );
    case 'espresso':
      return (
        <>
          <path d="M8 12h30L23 26z" fill="none" stroke={INK} strokeWidth="2" strokeLinejoin="round" />
          <path d="M23 26v12M16 40h14" stroke={INK} strokeWidth="2" strokeLinecap="round" />
        </>
      );
    case 'happy':
      return (
        <>
          <path d="M13 16h18l-2 24H15z" fill="none" stroke={INK} strokeWidth="2" />
          <path d="M26 16L33 4" stroke={INK} strokeWidth="1.5" />
          <path d="M26 6a9 9 0 0 1 16 4z" fill="#e2708a" stroke={INK} strokeWidth="1.2" />
          <circle cx="19" cy="27" r="1" fill={INK} />
          <circle cx="25" cy="27" r="1" fill={INK} />
          <path d="M18 31q4 4 8 0" fill="none" stroke={INK} strokeWidth="1.5" strokeLinecap="round" />
        </>
      );
    case 'lastcall':
      return (
        <>
          <path d="M15 20h14l-2 18H17z" fill="none" stroke={INK} strokeWidth="2" />
          <path d="M31 6a7 7 0 0 1 7 7v4l2 3H24l2-3v-4a7 7 0 0 1 5-7z" fill="#c9a45c" stroke={INK} strokeWidth="1.5" />
          <circle cx="32" cy="22" r="1.6" fill={INK} />
        </>
      );
    case 'topshelf':
      return (
        <>
          <path d="M6 40h36" stroke="#a8873c" strokeWidth="3" />
          <path d="M20 38V20l-2-3v-5h8v5l-2 3v18z" fill="none" stroke={INK} strokeWidth="1.5" />
          <rect x="20" y="24" width="8" height="7" fill="#f3ead7" />
          <path d="M17 8l3 3 4-5 4 5 3-3-1 6H18z" fill="#e0b85e" stroke={INK} strokeWidth="1" />
        </>
      );
  }
}

/** `drinking`: play the tip-and-drain once (set when this drink is ordered).
 *  `empty`: the glass stays empty after drinking (user, 2026-09-27): while the
 *  drink is going or sold out; the tip-and-drain still shows the liquid. */
export function DrinkIcon({ id, size = 40, drinking = false, empty = false }: { id: DrinkId; size?: number; drinking?: boolean; empty?: boolean }) {
  return (
    <svg className={`drink-icon${drinking ? ' drinking' : ''}`} width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <g className={`liquid${empty && !drinking ? ' gone' : ''}`}>
        <Liquid id={id} />
      </g>
      <Glass id={id} />
    </svg>
  );
}
